import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { environment } from "@env";

export interface DataMessageEvent {
	status?: "NEW" | "DELIVERED" | "CANCELED";
	platformId?: "jsf" | "angular";
	intention: string;
	payload: unknown;
}

interface CustomMessageEvent extends MessageEvent {
	data: DataMessageEvent | string;
}

@Injectable({
	providedIn: "root"
})
export class PostMessageService {
	private readonly STATUS_DELIVERED = "DELIVERED";
	private readonly STATUS_NEW = "NEW";
	private readonly STATUS_CANCELED = "CANCELED";
	private readonly PLATFORM_ID = "angular";
	private _iframe: HTMLIFrameElement | null = null;

	onNewMessage$: Subject<DataMessageEvent> = new Subject();

	constructor() {}

	setIframe(iframe: HTMLIFrameElement): void {
		if (this._srcIsJsf(iframe.src)) {
			this._iframe = iframe;
		}
	}

	sendMessageToIframe(message: DataMessageEvent): void {
		const messageToSend = {
			...message,
			status: message?.status ?? this.STATUS_NEW,
			platformId: this.PLATFORM_ID
		};

		this._iframe?.contentWindow?.postMessage(
			JSON.stringify(messageToSend),
			this._getTargetOrigin()
		);
	}

	listenMessage(): void {
		window.addEventListener("message", this._messageListener);
	}

	removeListener(): void {
		window.removeEventListener("message", this._messageListener);
	}

	private _messageListener = (event: CustomMessageEvent) => {
		const data = this._parseDataMessageEvent(event);

		if (this._isExternalMessage(event, data)) {
			this.onNewMessage$.next(data);
			this._replyToOriginDelivered(event);
		}
	};

	private _isExternalMessage(
		event: CustomMessageEvent,
		data: DataMessageEvent
	): boolean {
		return (
			event.origin !== location.origin ||
			data.platformId !== this.PLATFORM_ID
		);
	}

	private _replyToOriginDelivered(event: CustomMessageEvent) {
		const data = this._parseDataMessageEvent(event);

		if (data.status === this.STATUS_NEW) {
			const updatedData = {
				...data,
				status: this.STATUS_DELIVERED,
				platformId: this.PLATFORM_ID
			};

			event?.source?.postMessage(
				JSON.stringify(updatedData),
				(event.origin || "*") as WindowPostMessageOptions
			);
		}
	}

	private _srcIsJsf(url: string): boolean {
		return url.includes(".jsf");
	}

	private _getTargetOrigin(): string {
		return environment?.URL_BASEERP?.replace("/erp", "") || "*";
	}

	private _parseDataMessageEvent(
		event: CustomMessageEvent
	): DataMessageEvent {
		return (
			typeof event.data === "string" && this._isJsonString(event.data)
				? JSON.parse(event.data)
				: event.data
		) as DataMessageEvent;
	}

	private _isJsonString(str: string): boolean {
		try {
			const parsed = JSON.parse(str) as DataMessageEvent;
			return typeof parsed === "object" && parsed !== null;
		} catch (e) {
			return false;
		}
	}
}
